import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type TabKey = 'chrome' | 'iphone';
type Step = { src: string; title: string };

const tabs: { key: TabKey; label: string; shortLabel: string }[] = [
  { key: 'chrome', label: 'Google Password Manager', shortLabel: 'Chrome' },
  { key: 'iphone', label: 'iPhone Passwords app', shortLabel: 'iPhone' },
];

const chromeSteps: Step[] = [
  { src: '/tips-images/chrome-step-1.jpg', title: 'Open the Chrome browser' },
  {
    src: '/tips-images/chrome-step-2.jpg',
    title: 'Click on Chrome → Settings',
  },
  {
    src: '/tips-images/chrome-step-3.jpg',
    title: 'Click on Autofill and passwords',
  },
  {
    src: '/tips-images/chrome-step-4.jpg',
    title: 'Click on Google Password Manager',
  },
  {
    src: '/tips-images/chrome-step-5.jpg',
    title: 'Click on the search button',
  },
  { src: '/tips-images/chrome-step-6.jpg', title: 'Type to search for amazon' },
  { src: '/tips-images/chrome-step-7.jpg', title: 'Open the Amazon entry' },
  {
    src: '/tips-images/chrome-step-8.jpg',
    title: 'Tap the eye icon to unmask your password',
  },
];

const iphoneSteps: Step[] = [
  {
    src: '/tips-images/iphone-step-1.jpg',
    title: 'Search to find the Passwords app',
  },
  { src: '/tips-images/iphone-step-2.jpg', title: 'Open the Passwords app' },
  { src: '/tips-images/iphone-step-3.jpg', title: 'Search for amazon' },
  { src: '/tips-images/iphone-step-4.jpg', title: 'Open the Amazon entry' },
  {
    src: '/tips-images/iphone-step-5.jpg',
    title: 'Tap to reveal the password',
  },
];

const tabFromHash = (): TabKey =>
  window.location.hash.slice(1) === 'chrome' ? 'chrome' : 'iphone';

export function TipsFindPasswordPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>(tabFromHash);
  const steps = tab === 'chrome' ? chromeSteps : iphoneSteps;
  const isPhone = tab === 'iphone';

  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleTabChange = (key: TabKey) => {
    setTab(key);
    window.history.replaceState(null, '', `#${key}`);
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      window.close();
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center py-10 sm:py-12 px-6">
      <div className="w-full max-w-[640px]">
        {/* Back link */}
        <button
          type="button"
          data-testid="tips-back-button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-[14px] font-medium text-ink-muted hover:text-primary transition-colors mb-7"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Back to sign-in
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-primary mb-3">
            Help · Account linking
          </div>
          <h1 className="font-display text-[clamp(24px,4.5vw,32px)] font-bold text-ink leading-[1.2] tracking-[-0.025em] mb-3">
            How to find your Amazon password
          </h1>
          <p className="text-[15px] sm:text-[16px] text-ink-muted leading-[1.6]">
            Most people have their Amazon password saved in a password manager.
            Pick the one you use below to look it up, then head back to the
            sign-in page to finish linking your account.
          </p>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          className="flex w-full sm:w-auto sm:inline-flex bg-paper border border-rule-light rounded-xl p-1 mb-8 gap-1"
        >
          {tabs.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`tips-tab-${t.key}`}
                onClick={() => handleTabChange(t.key)}
                className={`flex-1 sm:flex-initial px-3 sm:px-[18px] py-2.5 rounded-[9px] font-body text-[13px] sm:text-[14px] inline-flex items-center justify-center gap-2 transition-all ${
                  active
                    ? 'bg-surface text-ink font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.06),0_0_0_1px_rgba(0,0,0,0.04)]'
                    : 'bg-transparent text-ink-muted font-medium hover:text-ink'
                }`}
              >
                {t.key === 'chrome' ? (
                  <svg
                    className="w-[15px] h-[15px] flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-[15px] h-[15px] flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"
                    />
                  </svg>
                )}
                <span className="sm:hidden">{t.shortLabel}</span>
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Steps */}
        <ol
          key={tab}
          className="flex flex-col gap-8 sm:gap-10 [animation:enter_0.3s_ease_both] list-none"
        >
          {steps.map((step, i) => (
            <li key={step.src} className="flex flex-col gap-3">
              <h3 className="font-display text-[15px] sm:text-[16px] font-semibold text-ink leading-snug">
                <span className="text-primary">Step {i + 1}</span>
                <span className="text-ink-faint mx-2">·</span>
                {step.title}
              </h3>
              {isPhone ? (
                <div className="mx-auto w-full max-w-[240px]">
                  <div className="relative bg-[#111] rounded-[44px] p-[10px] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.35),0_8px_16px_-8px_rgba(0,0,0,0.15)]">
                    <div className="relative overflow-hidden rounded-[34px] bg-surface">
                      <img
                        src={step.src}
                        alt={step.title}
                        loading={i === 0 ? 'eager' : 'lazy'}
                        className="w-full h-auto block"
                      />
                      <div
                        aria-hidden="true"
                        className="absolute top-2 left-1/2 -translate-x-1/2 w-[78px] h-[22px] bg-black rounded-full pointer-events-none"
                      />
                    </div>
                  </div>
                </div>
              ) : i < 3 ? (
                <div className="mx-auto w-full">
                  <div className="bg-black rounded-[12px] p-[6px] sm:p-[9px] shadow-[0_20px_40px_-14px_rgba(0,0,0,0.22),0_8px_16px_-8px_rgba(0,0,0,0.08)]">
                    <div className="relative overflow-hidden rounded-[4px] bg-surface">
                      <img
                        src={step.src}
                        alt={step.title}
                        loading={i === 0 ? 'eager' : 'lazy'}
                        className="w-full h-auto block"
                      />
                      <div
                        aria-hidden="true"
                        className="absolute top-0 left-1/2 -translate-x-1/2 w-[12%] aspect-[7/1] bg-black rounded-b-[4px] pointer-events-none"
                      />
                    </div>
                  </div>
                  <div
                    aria-hidden="true"
                    className="relative h-[10px] -mx-[2%] bg-gradient-to-b from-[#d4d4d4] via-[#b4b4b4] to-[#8a8a8a] rounded-b-[10px]"
                  >
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[16%] h-[5px] bg-[#7a7a7a] rounded-b-[3px]" />
                  </div>
                </div>
              ) : (
                <img
                  src={step.src}
                  alt={step.title}
                  loading="lazy"
                  className="w-full rounded-xl border border-rule-light bg-surface shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
                />
              )}
            </li>
          ))}
        </ol>

        {/* Done card */}
        <div className="mt-10 sm:mt-14 p-5 sm:p-7 bg-paper border border-rule-light rounded-2xl flex gap-4 items-start">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-[10px] bg-success-pale flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-success"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12.75l6 6 9-13.5"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <strong className="block font-display text-[15px] sm:text-[16px] font-semibold text-ink mb-1">
              Got your password?
            </strong>
            <span className="block text-[13px] sm:text-[14px] text-ink-muted leading-[1.55]">
              Head back to the sign-in page and enter your Amazon email and
              password to finish linking your account.
            </span>
            <button
              type="button"
              data-testid="tips-done-back-link"
              onClick={handleBack}
              className="mt-3 inline-flex items-center gap-1 text-[13px] sm:text-[14px] font-semibold text-primary hover:underline"
            >
              Back to sign-in →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
